import React, { useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import ContactForm from '../components/ContactForm';

const AboutPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="animate-in fade-in duration-700 pt-32 pb-12">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-8">
          <span className="text-sm font-bold text-blue-400 uppercase tracking-widest">Our Story</span>
        </div>
        <h1 className="text-5xl md:text-6xl font-extrabold text-white tracking-tight mb-6 leading-tight">
          About AutoReception & <span className="text-blue-500">the Founder</span>
        </h1>
      </section>

      {/* Content Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Column */}
          <div className="relative">
            <div className="absolute inset-0 bg-blue-600/20 blur-[100px] rounded-full pointer-events-none"></div>
            <img 
              src="https://media-manager-c.questera.ai/greta-media/9156b7ad9768dabbd1486a5021f9991fb512a0d6d6c844e6b5b41380a57b274ba7bc4b27b2a4de1f0a85d0c05b5620e1/images/aW1hZ2UvanBlZw==/6d0580e3cb5f7fc5c0c8317e96959b5f.jfif" 
              alt="Kasper Skov Jensen - Founder" 
              className="relative z-10 w-full max-w-md mx-auto aspect-[4/5] object-cover rounded-3xl border border-slate-700 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
            />
          </div>

          {/* Text Column */}
          <div className="space-y-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <SafeIcon name="Target" className="text-blue-500" />
                Why I Built AutoReception
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed text-lg">
                <p>
                  For more than 15 years, I have worked with digital communication, conversion rate optimization (CRO), and automation. Across e-commerce, media, and local service businesses, I kept seeing the exact same expensive bottleneck: missed phone calls.
                </p>
                <p>
                  When a potential client calls a business during a meeting, while on a job, or outside opening hours, and gets sent to voicemail, they rarely leave a message. They hang up and call a competitor instead.
                </p>
                <div className="border-l-4 border-blue-500 pl-6 py-2 my-8 bg-blue-500/5 rounded-r-xl">
                  <p className="text-white font-semibold text-xl italic">
                    "I built AutoReception to permanently fix that conversion leak."
                  </p>
                </div>
                <p>
                  By combining modern conversational AI with practical calendar and workflow automation, AutoReception ensures that every single inbound call is answered instantly, questions are answered accurately, and appointments are booked directly—24/7, without requiring extra staff or complex hardware.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <SafeIcon name="Briefcase" className="text-emerald-500" />
                Behind the Platform
              </h2>
              <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-8 space-y-8 shadow-xl">
                <p className="text-slate-300 text-lg border-b border-slate-800 pb-6">
                  AutoReception is founded and operated by Kasper Skov Jensen through the Danish digital consultancy <strong>Konsulentbixen</strong>.
                </p>
                
                <div className="flex gap-5">
                  <div className="w-14 h-14 bg-slate-800 rounded-2xl flex items-center justify-center shrink-0 border border-slate-700 shadow-inner">
                    <SafeIcon name="Award" className="text-2xl text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Background</h4>
                    <p className="text-slate-400 leading-relaxed">Formally trained journalist (DMJX) with over a decade of experience building automated media workflows, digital conversion systems, and partner platforms.</p>
                  </div>
                </div>

                <div className="flex gap-5">
                  <div className="w-14 h-14 bg-slate-800 rounded-2xl flex items-center justify-center shrink-0 border border-slate-700 shadow-inner">
                    <SafeIcon name="Compass" className="text-2xl text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Philosophy</h4>
                    <p className="text-slate-400 leading-relaxed">No bloated overhead, no gimmicks, and no generic chatbots that frustrate users. We focus solely on measurable ROI: turning missed incoming phone calls into confirmed bookings and captured revenue.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ContactForm />
    </div>
  );
};

export default AboutPage;